import { GAME_CONFIG } from "../config/gameConfig.js";
import { getNearestAlarmLightPos } from "./lighting.js";

/**
 * Positional audio helpers on top of KAPLAY play/pan/volume.
 * Silent no-op when a sound id is not loaded (assets land later under public/sounds/).
 *
 * Camera follows the dog — the listener is always screen-center. Use:
 * - playLocal — player/self SFX (always pan 0)
 * - playAt / playAttached — world emitters (always stereo-panned by world X vs dog)
 * - playUi — menus / terminal (pan 0, no falloff)
 *
 * Sound id registry (load when files exist): see docs/audio-events.md
 */

export const AUDIO_SOUNDS = {
    bite: "sfx_bite",
    bark: "sfx_bark",
    playerHurt: "sfx_player_hurt",
    playerDeath: "sfx_player_death",
    humanAlert: "sfx_human_alert",
    alarmLoop: "sfx_alarm_loop",
    alarmClear: "sfx_alarm_clear",
    robotWake: "sfx_robot_wake",
    robotAttack: "sfx_robot_attack",
    robotHit: "sfx_robot_hit",
    robotDeath: "sfx_robot_death",
    ramDrop: "sfx_ram_drop",
    ramPickup: "sfx_ram_pickup",
    memoryOpen: "sfx_memory_open",
    memoryClose: "sfx_memory_close",
    textboxAdvance: "sfx_textbox_advance",
    textboxType: "sfx_textbox_type",
    moduleInstall: "sfx_module_install",
    bootDiag: "sfx_boot_diag",
};

export const AUDIO_MUSIC = {
    battleDefault: "mus_battle_default",
    darkFactory: "mus_dark_factory",
    livelyCity: "mus_lively_city",
    hiddenCavern: "mus_hidden_cavern",
    spiritsForest: "mus_spirits_forest",
};

const CATEGORY_ALARM = "alarm";
const RESUMABLE_MUSIC = new Set([AUDIO_MUSIC.darkFactory]);

let listenerPos = { x: 0, y: 0 };
/** @type {Map<string, Set<object>>} */
const categoryHandles = new Map();
/** @type {Map<string, number>} */
const musicResumeTimes = new Map();
let currentMusicId = null;
let currentMusicHandle = null;
let storyMusicId = null;
/** 1 = full world bus, 0 = ducked (gameplay paused). */
let worldBusFade = 1;
let worldBusFadeTarget = 1;
let pauseFadeUpdater = null;

/**
 * Duck attached/world loops while Escape, memory, textbox, etc. pause gameplay.
 * Call each frame from the player (or game scene) — avoids importing pauseState here.
 */
export function setAudioWorldPaused(paused) {
    worldBusFadeTarget = paused ? 0 : 1;
    ensurePauseFadeUpdater();
}

export function getAudioWorldBusFade() {
    return worldBusFade;
}

function ensurePauseFadeUpdater() {
    if (pauseFadeUpdater || typeof onUpdate !== "function") return;
    pauseFadeUpdater = onUpdate(() => {
        const seconds = audioCfg().pauseFadeSeconds ?? 0.35;
        const step = seconds > 0 ? dt() / seconds : 1;
        if (worldBusFade < worldBusFadeTarget) {
            worldBusFade = Math.min(worldBusFadeTarget, worldBusFade + step);
        } else if (worldBusFade > worldBusFadeTarget) {
            worldBusFade = Math.max(worldBusFadeTarget, worldBusFade - step);
        }
    });
}

function audioCfg() {
    return GAME_CONFIG.audio || {};
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

/**
 * @param {string} soundId
 * @returns {boolean}
 */
export function hasSound(soundId) {
    if (!soundId || typeof getSound !== "function") return false;
    try {
        const asset = getSound(soundId);
        return Boolean(asset);
    } catch {
        return false;
    }
}

export function setAudioListener(worldPos) {
    if (!worldPos) return;
    listenerPos = { x: worldPos.x, y: worldPos.y };
}

export function getAudioListener() {
    return { x: listenerPos.x, y: listenerPos.y };
}

/**
 * Distance → volume (1 at/near nearDist, 0 at/beyond farDist) with smoothstep.
 */
export function volumeForDistance(dist, near = audioCfg().near, far = audioCfg().far) {
    const n = near ?? 48;
    const f = far ?? 420;
    if (dist <= n) return 1;
    if (dist >= f) return 0;
    const t = (dist - n) / Math.max(1, f - n);
    const s = t * t * (3 - 2 * t);
    return 1 - s;
}

export function panForOffsetX(dx, panScale = audioCfg().panScale) {
    const scale = panScale ?? 220;
    return clamp(dx / Math.max(1, scale), -1, 1);
}

function spatialOpts(worldPos, opts = {}) {
    const cfg = audioCfg();
    const maxVolume = opts.volume ?? cfg.maxVolume ?? 1;
    if (!worldPos) {
        return {
            ...opts,
            volume: 0,
            pan: 0,
        };
    }

    const dx = worldPos.x - listenerPos.x;
    const dy = worldPos.y - listenerPos.y;
    const dist = Math.hypot(dx, dy);
    const falloff = volumeForDistance(dist, opts.near ?? cfg.near, opts.far ?? cfg.far);
    // World sources always pan by X offset from the centered player listener.
    // (opts.pan is ignored — use playLocal / playUi for centered voices.)
    return {
        ...opts,
        volume: maxVolume * falloff,
        pan: panForOffsetX(dx, opts.panScale ?? cfg.panScale),
    };
}

function trackHandle(category, handle) {
    if (!category || !handle) return handle;
    if (!categoryHandles.has(category)) categoryHandles.set(category, new Set());
    categoryHandles.get(category).add(handle);
    const clear = () => {
        categoryHandles.get(category)?.delete(handle);
    };
    if (typeof handle.onEnd === "function") {
        handle.onEnd(clear);
    }
    return handle;
}

function safePlay(soundId, opts) {
    if (!hasSound(soundId)) return null;
    try {
        return play(soundId, opts);
    } catch {
        return null;
    }
}

function safePlayMusic(musicId, opts) {
    if (!musicId) return null;
    try {
        return play(musicId, opts);
    } catch {
        return null;
    }
}

function rememberCurrentMusicTime() {
    if (!currentMusicId || !currentMusicHandle || !RESUMABLE_MUSIC.has(currentMusicId)) return;
    try {
        const duration = typeof currentMusicHandle.duration === "function" ? currentMusicHandle.duration() : 0;
        const rawTime = typeof currentMusicHandle.time === "function" ? currentMusicHandle.time() : 0;
        if (!Number.isFinite(rawTime) || rawTime <= 0) return;
        const nextTime = duration > 0 ? rawTime % duration : rawTime;
        musicResumeTimes.set(currentMusicId, nextTime);
    } catch {
        // ignore
    }
}

export function stopMusic({ preserveTime = true } = {}) {
    if (preserveTime) rememberCurrentMusicTime();
    if (currentMusicHandle) {
        try {
            if (typeof currentMusicHandle.stop === "function") currentMusicHandle.stop();
            else currentMusicHandle.paused = true;
        } catch {
            // ignore
        }
    }
    currentMusicHandle = null;
    currentMusicId = null;
}

export function playMusic(musicId, opts = {}) {
    if (!musicId) {
        stopMusic();
        return null;
    }
    const volume = opts.volume ?? audioCfg().musicVolume ?? 0.32;
    if (currentMusicId === musicId && currentMusicHandle) {
        currentMusicHandle.volume = volume;
        return currentMusicHandle;
    }

    stopMusic();
    const resumeTime = RESUMABLE_MUSIC.has(musicId) ? musicResumeTimes.get(musicId) : null;
    const handle = safePlayMusic(musicId, {
        ...opts,
        loop: opts.loop !== false,
        seek: opts.seek ?? resumeTime ?? 0,
        volume,
        pan: 0,
    });
    currentMusicHandle = handle;
    currentMusicId = handle ? musicId : null;
    return handle;
}

export function setStoryMusic(musicId) {
    storyMusicId = musicId || null;
}

export function getStoryMusic() {
    return storyMusicId;
}

function makeBeepBuffer(ctx, { freq, duration, gain = 0.3 }) {
    const rate = ctx.sampleRate;
    const length = Math.max(1, Math.floor(rate * duration));
    const buffer = ctx.createBuffer(1, length, rate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
        const t = i / rate;
        const attack = Math.min(1, t * 120);
        const decay = Math.exp(-t * (freq > 700 ? 28 : 16));
        data[i] = Math.sin(2 * Math.PI * freq * t) * attack * decay * gain;
    }
    return buffer;
}

/** Short high click for seating a module into RAM. */
function makeClickBuffer(ctx, { duration = 0.055, gain = 0.26 } = {}) {
    const rate = ctx.sampleRate;
    const length = Math.max(1, Math.floor(rate * duration));
    const buffer = ctx.createBuffer(1, length, rate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
        const t = i / rate;
        const attack = Math.min(1, t * 260);
        const decay = Math.exp(-t * 86);
        const snap = Math.sin(2 * Math.PI * 1650 * t);
        const tick = Math.sin(2 * Math.PI * 2350 * t) * 0.45;
        data[i] = (snap + tick) * attack * decay * gain;
    }
    return buffer;
}

/** Procedural terminal beeps + module click (no asset files required). */
export function loadSynthesizedUiSounds() {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || typeof loadSound !== "function") return;
    try {
        const ctx = new AC();
        loadSound(AUDIO_SOUNDS.textboxType, makeBeepBuffer(ctx, {
            freq: 920,
            duration: 0.028,
            gain: 0.18,
        }));
        loadSound(AUDIO_SOUNDS.textboxAdvance, makeBeepBuffer(ctx, {
            freq: 480,
            duration: 0.07,
            gain: 0.28,
        }));
        loadSound(AUDIO_SOUNDS.moduleInstall, makeClickBuffer(ctx));
        loadSound(AUDIO_SOUNDS.bootDiag, makeBeepBuffer(ctx, {
            freq: 360,
            duration: 0.045,
            gain: 0.2,
        }));
    } catch {
        // WebAudio unavailable — synth SFX stay silent
    }
}

/** Soft per-character typewriter click. */
export function playTextboxType() {
    return playUi(AUDIO_SOUNDS.textboxType, { volume: 0.35 });
}

/** Confirm / skip beep when advancing a textbox. */
export function playTextboxAdvance() {
    return playUi(AUDIO_SOUNDS.textboxAdvance, { volume: 0.55 });
}

/** Click when seating a module into RAM. */
export function playModuleInstall() {
    return playUi(AUDIO_SOUNDS.moduleInstall, { volume: 0.5 });
}

/**
 * Boot terminal line blip — pitch climbs with each step (detune cents).
 * @param {number} step 0-based line index
 */
export function playBootDiagBeep(step = 0) {
    const capped = Math.min(Math.max(0, step), 28);
    return playUi(AUDIO_SOUNDS.bootDiag, {
        volume: 0.28,
        detune: capped * 55,
    });
}

/** Non-spatial UI / terminal SFX. */
export function playUi(soundId, opts = {}) {
    const cfg = audioCfg();
    return safePlay(soundId, {
        ...opts,
        volume: opts.volume ?? cfg.uiVolume ?? cfg.maxVolume ?? 0.7,
        pan: 0,
    });
}

/**
 * Player/self one-shot. Dog is camera-center — always mono (pan 0), no falloff.
 * Use for bite, bark, hurt, etc. World events must use playAt instead.
 */
export function playLocal(soundId, opts = {}) {
    const cfg = audioCfg();
    return safePlay(soundId, {
        ...opts,
        volume: opts.volume ?? cfg.maxVolume ?? 1,
        pan: 0,
    });
}

/** @type {Map<string, number>} last slice index per sound (avoid immediate repeats) */
const lastSliceIndex = new Map();

function pickRandomSlice(slices, soundId) {
    if (!slices?.length) return null;
    if (slices.length === 1) return slices[0];
    const prev = lastSliceIndex.get(soundId);
    let index = Math.floor(Math.random() * slices.length);
    if (prev != null && index === prev) {
        index = (index + 1 + Math.floor(Math.random() * (slices.length - 1))) % slices.length;
    }
    lastSliceIndex.set(soundId, index);
    return slices[index];
}

/**
 * Play a random time-slice from a multi-take sound file, then stop so later takes don't chain.
 * @param {string} soundId
 * @param {{ seek: number, duration: number }[]} slices
 */
export function playLocalSlice(soundId, slices, opts = {}) {
    const slice = pickRandomSlice(slices, soundId);
    if (!slice) return playLocal(soundId, opts);

    const handle = playLocal(soundId, {
        ...opts,
        seek: slice.seek ?? 0,
    });
    if (!handle) return null;

    const duration = Math.max(0.05, slice.duration ?? 0.25);
    wait(duration, () => {
        try {
            if (typeof handle.stop === "function") handle.stop();
            else handle.paused = true;
        } catch {
            // ignore
        }
    });
    return handle;
}

/** Dog bark — random take from sfx_bark.mp3 slices. */
export function playBarkLocal(opts = {}) {
    const slices = audioCfg().barkSlices;
    return playLocalSlice(AUDIO_SOUNDS.bark, slices, opts);
}

/** Spatial one-shot at a world emitter (always panned vs centered player). */
export function playAt(soundId, worldPos, opts = {}) {
    const playOpts = spatialOpts(worldPos, opts);
    if ((playOpts.volume ?? 0) <= 0.001) return null;
    return safePlay(soundId, playOpts);
}

/**
 * Looping / long SFX that tracks a moving source via getPos().
 * Returns a handle with stop(); refreshes pan/volume each frame while alive.
 */
export function playAttached(soundId, getPos, opts = {}) {
    if (!hasSound(soundId)) return null;

    const category = opts.category || null;
    const baseVolume = opts.volume;
    const loop = opts.loop !== false;
    const initialPos = typeof getPos === "function" ? getPos() : getPos;
    const handle = safePlay(soundId, {
        ...spatialOpts(initialPos, { ...opts, volume: baseVolume, loop }),
        loop,
    });
    if (!handle) return null;

    trackHandle(category, handle);
    ensurePauseFadeUpdater();

    const updater = onUpdate(() => {
        if (!handle || handle.paused === undefined) {
            updater.cancel?.();
            return;
        }
        try {
            const pos = typeof getPos === "function" ? getPos() : getPos;
            if (!pos) {
                handle.volume = 0;
                return;
            }
            const next = spatialOpts(pos, { ...opts, volume: baseVolume });
            const ducked = next.volume * worldBusFade;
            handle.pan = next.pan;
            if (ducked <= 0.01) {
                handle.volume = 0;
                handle.paused = true;
            } else {
                if (handle.paused) handle.paused = false;
                handle.volume = ducked;
            }
        } catch {
            updater.cancel?.();
            categoryHandles.get(category)?.delete(handle);
        }
    });

    const originalStop = typeof handle.stop === "function" ? handle.stop.bind(handle) : null;
    handle.stop = () => {
        updater.cancel?.();
        categoryHandles.get(category)?.delete(handle);
        if (originalStop) originalStop();
    };

    return handle;
}

export function stopCategory(category) {
    const set = categoryHandles.get(category);
    if (!set) return;
    for (const handle of Array.from(set)) {
        try {
            if (typeof handle.stop === "function") handle.stop();
            else if ("paused" in handle) handle.paused = true;
        } catch {
            // ignore
        }
    }
    set.clear();
}

export function stopAlarmAudio() {
    stopCategory(CATEGORY_ALARM);
}

/** Start / refresh room alarm loop at a world position (or moving getter). */
export function playAlarmAt(getPosOrPos) {
    stopAlarmAudio();
    return playAttached(AUDIO_SOUNDS.alarmLoop, getPosOrPos, {
        category: CATEGORY_ALARM,
        loop: true,
        volume: audioCfg().alarmVolume ?? 0.55,
    });
}

/**
 * Alarm loop tracks the nearest alarm strobe light (not the human).
 * Re-picks each frame so pan follows as the dog moves between strobes.
 * @param {number|null} [roomId] Prefer lights in the locked room.
 */
export function playAlarmFromLights(roomId = null) {
    return playAlarmAt(() => {
        const listener = getAudioListener();
        return getNearestAlarmLightPos(listener, roomId)
            ?? getNearestAlarmLightPos(listener, null);
    });
}

export function resetAudioForScene() {
    stopAlarmAudio();
    stopMusic({ preserveTime: false });
    musicResumeTimes.clear();
    categoryHandles.clear();
    storyMusicId = null;
    worldBusFade = 1;
    worldBusFadeTarget = 1;
    if (pauseFadeUpdater) {
        pauseFadeUpdater.cancel?.();
        pauseFadeUpdater = null;
    }
}
